import React from "react"
import { useTranslation } from "react-i18next"

import { ENTER_KEY } from "shared/lib/keys"
import { useEvent } from "shared/lib/useEvent"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TextInput } from "shared/ui/input/text/TextInput"

import { useGlobalSearchContext } from "../GlobalSearchContext"
import { useGlobalSearchInputContext } from "../GlobalSearchInputContext"
import { useGlobalSearchNavigationContext } from "../GlobalSearchNavigationContext"

export const GlobalSearchInput = React.memo(
  ({ autoFocus = false }: { autoFocus?: boolean }) => {
    const { t } = useTranslation()

    const { onSearchTermChange, hasResults, statusId, isLoading } =
      useGlobalSearchContext()
    const { inputValue, onInputValueChange } = useGlobalSearchInputContext()
    const { listboxId, activeIndex, getItemId, activateItem, onInputKeyDown } =
      useGlobalSearchNavigationContext()

    const handleChange = useEvent(
      (
        e:
          | React.FormEvent<HTMLInputElement>
          | React.KeyboardEvent<HTMLInputElement>
      ) => {
        if ("key" in e && e.key === ENTER_KEY.key) {
          return
        }

        onInputValueChange(e.currentTarget.value ?? "")
        onSearchTermChange(e.currentTarget.value || null)
      }
    )

    const activedescendant =
      activeIndex !== null ? getItemId(activeIndex) : undefined

    const handleBlur = useEvent((e: React.FocusEvent) => {
      const listbox = document.getElementById(listboxId)
      if (listbox && listbox.contains(e.relatedTarget as Node | null)) return
      activateItem(null)
    })

    const handleClear = useEvent(() => {
      onSearchTermChange("")
      onInputValueChange("")
    })

    return (
      <TextInput.Root label={t("GlobalSearch.InputLabel")}>
        <TextInput.Focus>
          <TextInput.Input
            value={inputValue}
            autoFocus={autoFocus}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={hasResults}
            aria-controls={listboxId}
            aria-describedby={statusId}
            aria-busy={isLoading}
            aria-activedescendant={activedescendant}
            onChange={handleChange}
            onKeyDown={onInputKeyDown}
            onBlur={handleBlur}
            placeholder={t("GlobalSearch.SearchPlaceholder")}
          />
          <TextInput.Clear value={inputValue} onChange={handleClear} />
          <TextInput.Affix>
            <Icon name={IconName.SEARCH} />
          </TextInput.Affix>
        </TextInput.Focus>
      </TextInput.Root>
    )
  }
)
